import React, { useState } from 'react';
import { Sparkles, Moon, Sun, Key, LogIn, LogOut, User, Menu, X, Crown } from 'lucide-react';
import ApiSettingsModal from './ApiSettingsModal';
import AuthModal from './AuthModal';
import UserProfile from './UserProfile';
import { useApiKeys } from '../contexts/ApiKeyContext';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionService } from '../services/subscription';

interface HeaderProps {
  darkMode: boolean;
  toggleDarkMode: () => void;
}

const Header: React.FC<HeaderProps> = ({ darkMode, toggleDarkMode }) => {
  const [showApiSettings, setShowApiSettings] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { apiKeys } = useApiKeys();
  const { user, profile, signOut, isAuthenticated } = useAuth();
  const configuredKeysCount = Object.keys(apiKeys).length;

  return (
    <>
    <header className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 shadow-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Logo Icon - Simple Sparkle */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-sky-500 rounded-xl blur-lg opacity-60 group-hover:opacity-100 transition-all"></div>
              <div className="relative bg-gradient-to-br from-cyan-500 to-sky-500 p-2.5 rounded-xl shadow-lg group-hover:shadow-xl transition-all">
                <Sparkles className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>
            </div>
            <div>
              <h1 className="text-lg sm:text-2xl font-bold text-white flex items-center">
                <span className="bg-gradient-to-r from-cyan-400 to-sky-400 bg-clip-text text-transparent font-extrabold">FX</span>
                <span className="text-white">Prompt</span>
                <span className="text-cyan-400 text-sm sm:text-base ml-1 font-normal">.app</span>
              </h1>
              <p className="text-xs sm:text-sm text-gray-300 hidden sm:block">Professional AI Prompt Generator for Creators</p>
            </div>
          </div>
          
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="sm:hidden p-2 rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors"
          >
            {mobileMenuOpen ? <X className="w-5 h-5 text-cyan-400" /> : <Menu className="w-5 h-5 text-cyan-400" />}
          </button>
          
          {/* Desktop Menu */}
          <div className="hidden sm:flex items-center gap-3">
            {/* API Settings */}
            <button
              onClick={() => setShowApiSettings(true)}
              className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-600 transition-all hover:scale-105 relative group"
              title="API Settings"
            >
              <Key className="w-5 h-5 text-cyan-400 group-hover:text-cyan-300" />
              {configuredKeysCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold animate-pulse">
                  {configuredKeysCount}
                </span>
              )}
            </button>
            
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-600 transition-all hover:scale-105 group"
              title={darkMode ? 'Light Mode' : 'Dark Mode'}
            >
              {darkMode ? 
                <Sun className="w-5 h-5 text-yellow-400 group-hover:text-yellow-300" /> : 
                <Moon className="w-5 h-5 text-cyan-400 group-hover:text-cyan-300" />
              }
            </button>
            
            {/* Divider */}
            <div className="w-px h-8 bg-slate-600"></div>
            
            {/* User Profile/Auth Button */}
            {isAuthenticated && user ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowProfile(true)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-slate-700/70 to-slate-600/70 rounded-full hover:from-slate-600 hover:to-slate-500 transition-all hover:scale-105 border border-slate-600/50"
                >
                  {user.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt="User Avatar"
                      className="w-7 h-7 rounded-full border-2 border-cyan-400/50 object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || user.email || 'U')}&background=06b6d4&color=fff&size=32`;
                      }}
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-full border-2 border-cyan-400/50 bg-gradient-to-br from-cyan-500 to-sky-500 flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">
                        {(user.displayName || user.email || 'U')[0].toUpperCase()}
                      </span>
                    </div>
                  )}
                  <span className="text-sm font-medium text-gray-200 max-w-[120px] truncate">
                    {user.displayName || user.email?.split('@')[0]}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-bold shadow-sm flex items-center gap-1 ${
                    subscriptionService.isPro(profile)
                      ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white'
                      : 'bg-gradient-to-r from-gray-500 to-gray-600 text-white'
                  }`}>
                    {subscriptionService.isPro(profile) && <Crown className="w-3 h-3" />}
                    {subscriptionService.formatTierName(subscriptionService.getUserTier(profile))}
                  </span>
                </button>
                
                <button
                  onClick={() => signOut()}
                  className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 transition-all hover:scale-105 group"
                  title="Sign Out"
                >
                  <LogOut className="w-5 h-5 text-red-400 group-hover:text-red-300" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowAuthModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-sky-500 hover:from-cyan-600 hover:to-sky-600 rounded-lg transition-all hover:scale-105 shadow-lg hover:shadow-cyan-500/25"
              >
                <LogIn className="w-5 h-5 text-white" />
                <span className="font-medium text-white">Sign In</span>
              </button>
            )}
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="sm:hidden mt-4 pb-4 border-t border-slate-700">
            <div className="flex flex-col gap-2 mt-4">
              {isAuthenticated && user ? (
                <>
                  <button
                    onClick={() => {
                      setShowProfile(true);
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 px-3 py-2.5 bg-gradient-to-r from-slate-700/70 to-slate-600/70 rounded-lg hover:from-slate-600 hover:to-slate-500 transition-colors"
                  >
                    {user.photoURL ? (
                      <img
                        src={user.photoURL}
                        alt="User Avatar"
                        className="w-8 h-8 rounded-full border-2 border-cyan-400/50 object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || user.email || 'U')}&background=06b6d4&color=fff&size=32`;
                        }}
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full border-2 border-cyan-400/50 bg-gradient-to-br from-cyan-500 to-sky-500 flex items-center justify-center">
                        <span className="text-white font-semibold text-base">
                          {(user.displayName || user.email || 'U')[0].toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 text-left">
                      <span className="text-sm font-medium text-gray-200 block">
                        {user.displayName || user.email?.split('@')[0]}
                      </span>
                      <span className="text-xs text-gray-400">View Profile</span>
                    </div>
                    {profile?.subscription === 'pro' && (
                      <span className="text-xs bg-gradient-to-r from-yellow-400 to-amber-500 text-black px-2 py-0.5 rounded-full font-bold">
                        PRO
                      </span>
                    )}
                  </button>
                  
                  <div className="h-px bg-slate-700 my-1"></div>
                  
                  <button
                    onClick={() => {
                      setShowApiSettings(true);
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 px-3 py-2 bg-slate-700/50 rounded-lg hover:bg-slate-600 transition-colors"
                  >
                    <Key className="w-5 h-5 text-cyan-400" />
                    <span className="text-sm text-gray-200 flex-1 text-left">API Settings</span>
                    {configuredKeysCount > 0 && (
                      <span className="bg-green-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                        {configuredKeysCount}
                      </span>
                    )}
                  </button>
                  
                  <button
                    onClick={() => {
                      toggleDarkMode();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 px-3 py-2 bg-slate-700/50 rounded-lg hover:bg-slate-600 transition-colors"
                  >
                    {darkMode ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5 text-cyan-400" />}
                    <span className="text-sm text-gray-200 flex-1 text-left">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
                  </button>
                  
                  <div className="h-px bg-slate-700 my-1"></div>
                  
                  <button
                    onClick={() => {
                      signOut();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 px-3 py-2 bg-red-500/20 rounded-lg hover:bg-red-500/30 transition-colors"
                  >
                    <LogOut className="w-5 h-5 text-red-400" />
                    <span className="text-sm text-red-400 flex-1 text-left">Sign Out</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setShowAuthModal(true);
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 px-3 py-2.5 bg-gradient-to-r from-cyan-500 to-sky-500 hover:from-cyan-600 hover:to-sky-600 rounded-lg transition-colors"
                  >
                    <LogIn className="w-5 h-5 text-white" />
                    <span className="text-sm font-medium text-white">Sign In</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowApiSettings(true);
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 px-3 py-2 bg-slate-700/50 rounded-lg hover:bg-slate-600 transition-colors"
                  >
                    <Key className="w-5 h-5 text-cyan-400" />
                    <span className="text-sm text-gray-200 flex-1 text-left">API Settings</span>
                    {configuredKeysCount > 0 && (
                      <span className="bg-green-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                        {configuredKeysCount}
                      </span>
                    )}
                  </button>
                  
                  <button
                    onClick={() => {
                      toggleDarkMode();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 px-3 py-2 bg-slate-700/50 rounded-lg hover:bg-slate-600 transition-colors"
                  >
                    {darkMode ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5 text-cyan-400" />}
                    <span className="text-sm text-gray-200 flex-1 text-left">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
    {showApiSettings && (
      <ApiSettingsModal onClose={() => setShowApiSettings(false)} />
    )}
    {showAuthModal && (
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    )}
    {showProfile && (
      <UserProfile onClose={() => setShowProfile(false)} />
    )}
    </>
  );
};

export default Header;